import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const COOKIE_CONSENT_KEY = 'biosounds_cookie_consent';

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if user has already given consent
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to avoid showing immediately on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: true,
      timestamp: new Date().toISOString()
    }));
    window.dispatchEvent(new Event('biosounds-consent'));
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      accepted: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const content = {
    es: {
      title: 'Utilizamos cookies',
      description: 'Usamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y personalizar contenido. Al hacer clic en "Aceptar", consientes el uso de todas las cookies.',
      accept: 'Aceptar',
      reject: 'Rechazar',
      moreInfo: 'Política de Cookies'
    },
    en: {
      title: 'We use cookies',
      description: 'We use our own and third-party cookies to improve your experience, analyze traffic and personalize content. By clicking "Accept", you consent to the use of all cookies.',
      accept: 'Accept',
      reject: 'Reject',
      moreInfo: 'Cookie Policy'
    }
  };

  const t = content[language] || content.es;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-[#0a0f1a]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-black/50">
              {/* Close button */}
              <button
                onClick={handleReject}
                className="absolute top-3 right-3 p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Close"
                data-testid="cookie-close-btn"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Icon */}
                <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#0AFFD6]/20 to-[#0B8FFF]/20 border border-[#0AFFD6]/30 flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-[#0AFFD6]" />
                </div>

                {/* Content */}
                <div className="flex-1 pr-6 sm:pr-0">
                  <h3 className="text-white font-semibold font-['Outfit'] mb-1">
                    {t.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {t.description}{' '}
                    <Link 
                      to="/cookies" 
                      className="text-[#0AFFD6] hover:text-[#0AFFD6]/80 underline underline-offset-2"
                      data-testid="cookie-policy-link"
                    >
                      {t.moreInfo}
                    </Link>
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    className="flex-1 sm:flex-none px-5 py-2.5 border-white/20 text-slate-300 hover:bg-white/10 hover:text-white"
                    data-testid="cookie-reject-btn"
                  >
                    {t.reject}
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="flex-1 sm:flex-none px-5 py-2.5 bg-[#0AFFD6] text-[#030712] hover:bg-[#0AFFD6]/90 font-medium"
                    data-testid="cookie-accept-btn"
                  >
                    {t.accept}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
